import getDecorators from "inversify-inject-decorators";
import container from "../../../ioc/inversify.config";
import TYPES from "../../../ioc/types";
import { GameConfigurationDTO, PlayerCharacterDTO } from "../../../shared/dtos";
import { ThemeManager } from "../../design-system/theme-manager";
import { DefaultSelectable } from "../../menu/interfaces";
import { BoxOptions } from "../../menu/message-box";
import { GeometryService } from "../../ui-services/geometry-ui-service";
import { buildStatText, buildTextStyle, calculateRequiredXP } from "../../utils/text-style";
import { TextContent } from "../text-content";

const { lazyInject } = getDecorators(container);

export class PlayerCharacterCard extends DefaultSelectable {
    @lazyInject(TYPES.GeometryService)
    private geometryService: GeometryService;

    @lazyInject(TYPES.ThemeManager)
    private themeManager: ThemeManager;

    private paddingY: number;
    private paddingX: number;

    private playerCharacter: PlayerCharacterDTO;

    constructor(
        x: number,
        y: number,
        width: number,
        height: number,
        playerCharacter: PlayerCharacterDTO,
        scene: Phaser.Scene
    ) {
        super(scene, x, y);
        this.playerCharacter = playerCharacter;

        // Initialize padding from theme
        this.paddingY = this.themeManager.getComponentSpacing("paddingSmall");
        this.paddingX = this.themeManager.getComponentSpacing("paddingSmall");

        const lineHeight = 16;
        let currentY = y + this.paddingY;

        const characterName = new TextContent(
            x + this.paddingX,
            currentY,
            playerCharacter.name,
            scene
        );
        currentY += lineHeight;

        const gameConfigJson = scene.cache.json.get("game_config") as GameConfigurationDTO;
        const gameConfig = GameConfigurationDTO.fromGameConfiguration(gameConfigJson);

        const graphics = this.scene.add.graphics();
        const level = new TextContent(
            x + this.paddingX,
            currentY,
            playerCharacter.character_class + " level " + playerCharacter.level,
            scene
        );
        currentY += lineHeight;

        const hpLabel = gameConfig.findStatDefinitionById("hp");
        const atkLabel = gameConfig.findStatDefinitionById("atk");

        const playerHp = playerCharacter.findStatById("hp");
        const playerMaxHp = playerCharacter.findStatById("max_hp");
        const atk = playerCharacter.findStatById("atk");

        const hpString =
            hpLabel && playerHp && playerMaxHp
                ? buildStatText(hpLabel, playerHp, playerMaxHp)
                : null;
        const hps =
            hpString !== null
                ? new TextContent(x + this.paddingX, currentY, hpString, scene)
                : null;
        if (hps) currentY += lineHeight;

        const atkString = buildStatText(atkLabel, atk);
        const dmg =
            atkString !== null
                ? new TextContent(x + this.paddingX, currentY, atkString, scene)
                : null;
        if (dmg) currentY += lineHeight;

        // Create XP panel with progress bar
        const xpPanel = this.createXPPanel(
            x + this.paddingX,
            currentY,
            width - this.paddingX * 2,
            playerCharacter,
            gameConfig,
            scene
        );
        currentY += 32; // Height of XP panel (label: 16px + bar: 10px + spacing: 6px)

        this.add(graphics);
        this.add(characterName);
        this.add(level);
        if (hps) this.add(hps);
        if (dmg) this.add(dmg);
        this.add(xpPanel);
        this.geometryService.initGraphicContext(graphics);
        this.geometryService.drawBox(x, y, width, height, {
            lineAlpha: 0.8,
            fillAlpha: 0.3,
        } as BoxOptions);

        if (!this.playerCharacter.alive) {
            this.checkDeadCharacters();
        }
    }

    public unselect() {
        super.unselect();
        this.checkDeadCharacters();
    }

    public checkDeadCharacters() {
        this.each((elem, index) => {
            if (elem instanceof TextContent) {
                if (!this.playerCharacter.alive) {
                    elem.setColor(this.themeManager.getColorString("accent.red"));
                }
            }
        });
    }

    /**
     * Create XP panel with progress bar
     */
    private createXPPanel(
        x: number,
        y: number,
        width: number,
        character: PlayerCharacterDTO,
        gameConfig: GameConfigurationDTO,
        scene: Phaser.Scene
    ): Phaser.GameObjects.Container {
        const container = scene.add.container(0, 0);

        // Check if at max level
        const maxLevel = gameConfig.max_level || 99;
        const isMaxLevel = (character.level || 1) >= maxLevel;

        // Get required XP for next level
        const requiredXP = isMaxLevel ? 1 : calculateRequiredXP(character.level || 1, gameConfig);
        const currentXP = character.xp || 0;
        const progress = isMaxLevel ? 1 : Math.min(currentXP / requiredXP, 1); // Cap at 100%
        const xpToNextLevel = isMaxLevel ? 0 : Math.max(requiredXP - currentXP, 0);

        const barWidth = width - 10;
        const barHeight = 10;

        console.log(
            `${JSON.stringify({ ...buildTextStyle(this.themeManager, { color: this.themeManager.getColorString("text.secondary") }) })}`
        );

        // XP Label
        const label = scene.add.text(x, y, "Experience:", {
            ...buildTextStyle(this.themeManager, {
                color: this.themeManager.getColorString("text.secondary"),
            }),
        });
        label.setOrigin(0, 0);
        container.add(label);

        // XP Bar Background (using lineHeight spacing: 16px)
        const barBg = scene.add.rectangle(x, y + 16, barWidth, barHeight, 0x333333);
        barBg.setOrigin(0, 0);
        container.add(barBg);

        // XP Bar Fill
        const fillWidth = Math.max(barWidth * progress, 2); // Minimum 2px to show some progress
        const barFill = scene.add.rectangle(x, y + 16, fillWidth, barHeight, 0x3498db);
        barFill.setOrigin(0, 0);
        container.add(barFill);

        // XP Text (centered on bar) - using theme colors
        const xpText = scene.add.text(
            x + barWidth / 2,
            y + 21,
            isMaxLevel ? "MAX LEVEL" : `${currentXP} / ${requiredXP}`,
            {
                ...buildTextStyle(this.themeManager, {
                    color: isMaxLevel
                        ? this.themeManager.getColorString("accent.yellow")
                        : this.themeManager.getColorString("text.primary"),
                    fontSize: "10px",
                }),
            }
        );
        xpText.setOrigin(0.5);
        container.add(xpText);

        // Visual indicator when close to level-up (90%+)
        if (progress >= 0.9 && xpToNextLevel > 0) {
            const nearLevelUpIcon = scene.add.text(x + barWidth + 5, y + 16, "⚡", {
                ...buildTextStyle(this.themeManager, {
                    color: this.themeManager.getColorString("accent.yellow"),
                    fontSize: "14px",
                }),
            });
            container.add(nearLevelUpIcon);
        }

        return container;
    }
}
